import { Component, Input, OnInit } from '@angular/core';
import { MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { Observable } from 'rxjs';
import { StringHelper } from 'src/app/helpers/string-helper';
import UserHelper from 'src/app/helpers/user-helper';
import { Adjustment } from 'src/app/model/project/adjustment';
import { SoldRented } from 'src/app/model/project/sold-rented';
import { SUB_PROJECT_TYPES } from 'src/app/model/project/sub-project';
import {
  GetPrintworkAndAtelierInfoForSubProjectControllerRequestService,
  PrintworkAndAtelierInfo,
} from 'src/app/services/api/immo/get-printwork-and-atelier-info/get-printwork-and-atelier-info-for-subproject-controller-request.service';
import { ResetAtelierProgressControllerRequestService } from 'src/app/services/api/immo/reset-atelier-progress/reset-atelier-progress-controller-request.service';
import { ResetPrintworkProgressControllerRequestService } from 'src/app/services/api/immo/reset-printwork-progress/reset-printwork-progress-controller-request.service';
import { AuthService } from 'src/app/services/auth.service';
import { ModalService } from 'src/app/services/modal.service';
import { SnackService } from 'src/app/services/snack.service';
import { SetAdjustmentNeedsPrintworkControllerRequestService } from './set-adjustment-needs-printwork-controller-request.service';

@Component({
  selector: 'client-adjustment-printwork-dialog',
  templateUrl: './client-adjustment-printwork-dialog.component.html',
  styleUrls: ['./client-adjustment-printwork-dialog.component.scss'],
})
export class ClientAdjustmentPrintworkDialogComponent implements OnInit {
  @Input()
  adjustment: Adjustment;

  soldRented: SoldRented;

  needPrintworkChecked: boolean;
  printworkExtraInfo: string;
  atelierExtraInfo: string;
  handleByAtelier: boolean;
  createNewBoard: boolean;
  subBoardsRequired: boolean;

  printStickerChecked: boolean;
  printStickerCheckedLabel: string;

  init: boolean = false;
  submitting: boolean = false;

  printworkAndAtelierInfo: PrintworkAndAtelierInfo;

  resettingPrintwork: boolean = false;
  resettingAtelier: boolean = false;

  constructor(
    private setAdjustmentNeedsPrintworkControllerRequestService: SetAdjustmentNeedsPrintworkControllerRequestService,
    private getPrintworkAndAtelierInfoForSubProjectControllerRequestService: GetPrintworkAndAtelierInfoForSubProjectControllerRequestService,
    private resetPrintworkProgressControllerRequestService: ResetPrintworkProgressControllerRequestService,
    private resetAtelierProgressControllerRequestService: ResetAtelierProgressControllerRequestService,
    private snackService: SnackService,
    private modalService: ModalService,
    private authService: AuthService,
    private dialogRef: MatDialogRef<
      ClientAdjustmentPrintworkDialogComponent,
      boolean
    >
  ) {}

  ngOnInit(): void {
    this.needPrintworkChecked = this.adjustment.needPrintwork;
    this.handleByAtelier = this.adjustment.handleByAtelier;
    this.printworkExtraInfo = this.adjustment.printworkExtraInfo;
    this.atelierExtraInfo = this.adjustment.atelierExtraInfo;
    this.createNewBoard = this.adjustment.createNewBoard;
    this.subBoardsRequired = this.adjustment.subBoardsRequired;

    if (this.adjustment.type === SUB_PROJECT_TYPES.SOLDRENTED) {
      this.soldRented = <SoldRented>this.adjustment;
      this.printStickerChecked = this.soldRented.printStickerChecked;
      this.calculatePrintStickerCheckedLabel();
    }

    if (!this.needPrintworkChecked) {
      this.initializeDefaultValues();
    }
    this.getPrintworkAndAtelierInfo();
    this.init = true;
  }

  private initializeDefaultValues(): void {
    if (this.soldRented != null) {
      this.printStickerChecked = !StringHelper.isEmpty(
        this.soldRented.otherSticker
      );
    }
  }

  private calculatePrintStickerCheckedLabel(): void {
    const stickerText =
      this.soldRented.signSticker != null
        ? this.soldRented.signSticker.text
        : this.soldRented.otherSticker;
    this.printStickerCheckedLabel =
      'Sticker' + (StringHelper.isEmpty(stickerText) ? '' : `: ${stickerText}`);
  }

  private async getPrintworkAndAtelierInfo(): Promise<void> {
    if (this.adjustment != null && this.adjustment.id != null) {
      this.printworkAndAtelierInfo =
        await this.getPrintworkAndAtelierInfoForSubProjectControllerRequestService.getPrintworkAndAtelierInfoForSubProject(
          this.adjustment.id
        );
    }
  }

  printworkExtraInfoChanged(value: string): void {
    this.printworkExtraInfo = value;
  }

  atelierExtraInfoChanged(value: string): void {
    this.atelierExtraInfo = value;
  }

  toggleCreateNewBoard(): void {
    this.createNewBoard = !!!this.createNewBoard;
  }

  toggleSubBoardsRequired(): void {
    this.subBoardsRequired = !!!this.subBoardsRequired;
  }

  closeDialog(): void {
    this.dialogRef.close(false);
  }

  getClosedObservable(): Observable<boolean> {
    return this.dialogRef.afterClosed();
  }

  async confirmNeedsPrintwork(): Promise<void> {
    this.submitting = true;

    const error = this.verifyNeedsPrintwork();

    if (error != null) {
      this.snackService.showError(error);
      this.submitting = false;
      return;
    }

    try {
      await this.setAdjustmentNeedsPrintworkControllerRequestService.setAdjustmentNeedsPrintwork(
        this.adjustment.id,
        this.needPrintworkChecked,
        this.printworkExtraInfo,
        this.atelierExtraInfo,
        this.printStickerChecked,
        this.handleByAtelier,
        this.createNewBoard,
        this.subBoardsRequired
      );

      this.adjustment.needPrintwork = this.needPrintworkChecked;
      this.adjustment.handleByAtelier = this.handleByAtelier;
      if (this.handleByAtelier) {
        this.adjustment.atelierExtraInfo = this.atelierExtraInfo;
        this.adjustment.createNewBoard = this.createNewBoard;
        this.adjustment.subBoardsRequired = this.subBoardsRequired;
      } else {
        this.adjustment.atelierExtraInfo = null;
      }
      if (this.needPrintworkChecked) {
        this.adjustment.printworkExtraInfo = this.printworkExtraInfo;
        this.adjustment.printWorkReady = false;
        if (this.soldRented != null) {
          this.soldRented.printStickerChecked = this.printStickerChecked;
        }
      } else {
        this.adjustment.printworkExtraInfo = null;
        if (this.soldRented != null) {
          this.soldRented.printStickerChecked = false;
        }
      }

      this.dialogRef.close(true);
    } finally {
      this.submitting = false;
    }
  }

  private verifyNeedsPrintwork(): string {
    if (!this.needPrintworkChecked) {
      return null;
    }
    if (this.soldRented == null) {
      return StringHelper.isEmpty(this.printworkExtraInfo)
        ? 'Gelieve een beschrijving in te vullen van het nodige printwerk'
        : null;
    } else {
      if (this.printStickerChecked) {
        return null;
      }
      return StringHelper.isEmpty(this.printworkExtraInfo)
        ? 'Gelieve een beschrijving in te vullen van het nodige printwerk'
        : null;
    }
  }

  resetPrintwork(): void {
    this.modalService.confirmDialog(
      'Printwerk resetten',
      'Bent u zeker dat u printwerk wilt resetten voor deze opdracht?',
      (b: boolean) => {
        if (b) {
          this.doResetPrintwork();
        }
      }
    );
  }

  private doResetPrintwork(): void {
    this.resettingPrintwork = true;
    this.resetPrintworkProgressControllerRequestService
      .resetPrintworkProgress(this.adjustment.id)
      .then(() => {
        this.adjustment.printWorkReady = false;
        if (this.printworkAndAtelierInfo != null) {
          this.printworkAndAtelierInfo.printWorkFinished = false;
        }
      })
      .finally(() => (this.resettingPrintwork = false));
  }

  resetAtelier(): void {
    this.modalService.confirmDialog(
      'Atelier resetten',
      'Bent u zeker dat u atelier wilt resetten voor deze opdracht?',
      (b: boolean) => {
        if (b) {
          this.doResetAtelier();
        }
      }
    );
  }

  private doResetAtelier(): void {
    this.resettingAtelier = true;
    this.resetAtelierProgressControllerRequestService
      .resetAtelierProgress(this.adjustment.id)
      .then(() => {
        if (this.printworkAndAtelierInfo != null) {
          this.printworkAndAtelierInfo.atelierFinished = false;
        }
      })
      .finally(() => (this.resettingAtelier = false));
  }

  impersonateAtelierEmployee(): void {
    const atelierEmployeeName = UserHelper.getDisplayName(
      this.printworkAndAtelierInfo.atelierEmployeeUser
    );
    this.modalService.confirmDialog(
      `${atelierEmployeeName} nabootsen`,
      `Bent u zeker dat u ${atelierEmployeeName} wilt nabootsen?`,
      (b: boolean) => {
        if (b) {
          this.doImpersonateAtelierEmployee();
        }
      }
    );
  }

  doImpersonateAtelierEmployee(): void {
    this.closeDialog();
    this.authService.impersonateUser(
      this.printworkAndAtelierInfo.atelierEmployeeUser
    );
  }
}
